/**
 * lighting-presets.js
 * Quản lý cấu hình ánh sáng, bóng đổ theo ngữ cảnh (Studio / Phố Phường) và theo giờ (Trưa / Hoàng Hôn / Đêm).
 */

import * as THREE from 'three';

export const LIGHTING_PRESETS = {
  noon: {
    name: 'Nắng Trưa',
    sky: { top: '#7dd3fc', bottom: '#e0f2fe' },
    fog: { color: '#bae6fd', density: 0.008 },
    hemiLight: { sky: 0xe0f2fe, ground: 0x64748b, intensity: 0.65 },
    ambientLight: { color: 0xffffff, intensity: 0.38 },
    dirLight: { color: 0xfffbeb, intensity: 2.1, position: [-12, 16, 4] },
    rimLight: { color: 0x38bdf8, intensity: 0.8, position: [-6, 4, -6] },
    fillLight: { color: 0xfffbeb, intensity: 0.55, position: [0, 3, 8] }
  },
  sunset: {
    name: 'Hoàng Hôn Anime',
    sky: { top: '#4c1d95', bottom: '#ea580c' },
    fog: { color: '#7c2d12', density: 0.012 },
    hemiLight: { sky: 0xfb923c, ground: 0x431407, intensity: 0.65 },
    ambientLight: { color: 0xfed7aa, intensity: 0.22 },
    dirLight: { color: 0xf97316, intensity: 2.4, position: [-14, 6, 3] },
    rimLight: { color: 0xf43f5e, intensity: 0.9, position: [-6, 4, -6] },
    fillLight: { color: 0xfde047, intensity: 0.45, position: [0, 3, 8] }
  },
  night: {
    name: 'Đêm Phố',
    sky: { top: '#020617', bottom: '#0f172a' },
    fog: { color: '#020617', density: 0.016 },
    hemiLight: { sky: 0x38bdf8, ground: 0x020617, intensity: 0.3 },
    ambientLight: { color: 0x38bdf8, intensity: 0.12 },
    dirLight: { color: 0x818cf8, intensity: 1.0, position: [-10, 14, 3] },
    rimLight: { color: 0x38bdf8, intensity: 0.7, position: [6, 4, 6] },
    fillLight: { color: 0x1e293b, intensity: 0.18, position: [0, 3, 8] }
  }
};

const skyTextureCache = new Map();

/**
 * Sinh CanvasTexture gradient 2x256 cho bầu trời đường phố
 */
export function createSkyGradientTexture(topColor, bottomColor) {
  const key = `${topColor}_${bottomColor}`;
  if (skyTextureCache.has(key)) {
    return skyTextureCache.get(key);
  }

  if (typeof document === 'undefined') {
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = 2;
  canvas.height = 256;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const gradient = ctx.createLinearGradient(0, 0, 0, 256);
  gradient.addColorStop(0, topColor);
  gradient.addColorStop(1, bottomColor);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 2, 256);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  skyTextureCache.set(key, texture);
  return texture;
}

/**
 * Thiết lập kích thước và vùng bóng đổ của Directional Light theo ngữ cảnh
 */
export function fitShadowCamera(state, options = {}) {
  const { dirLight, scene, renderer } = state;
  if (!dirLight || !scene) return;

  const center = options.center || [0, 1, 0];
  const halfSize = options.halfSize ?? 5;
  const near = options.near ?? 0.5;
  const far = options.far ?? 65;

  const cam = dirLight.shadow.camera;
  cam.left = -halfSize;
  cam.right = halfSize;
  cam.top = halfSize;
  cam.bottom = -halfSize;
  cam.near = near;
  cam.far = far;

  if (!dirLight.target.parent) {
    scene.add(dirLight.target);
  }
  dirLight.target.position.set(center[0], center[1], center[2]);
  dirLight.target.updateMatrixWorld();

  cam.updateProjectionMatrix();

  dirLight.shadow.normalBias = 0.02;
  dirLight.shadow.bias = -0.0005;

  let desiredMapSize = options.mapSize || 2048;
  if (renderer && renderer.capabilities) {
    desiredMapSize = Math.min(desiredMapSize, renderer.capabilities.maxTextureSize || 2048);
  }
  if (dirLight.shadow.mapSize.width !== desiredMapSize) {
    dirLight.shadow.mapSize.set(desiredMapSize, desiredMapSize);
    if (dirLight.shadow.map) {
      dirLight.shadow.map.dispose();
      dirLight.shadow.map = null;
    }
  }
}

/**
 * Áp dụng cấu hình ánh sáng và bầu trời cho Scene
 */
export function applyLightingPreset(state, time = 'noon') {
  const preset = LIGHTING_PRESETS[time] || LIGHTING_PRESETS.noon;
  const { scene, hemiLight, ambientLight, dirLight, rimLight, fillLight } = state;

  if (!scene || !hemiLight || !ambientLight || !dirLight) return;

  // 1. Cập nhật các nguồn sáng
  hemiLight.color.set(preset.hemiLight.sky);
  hemiLight.groundColor.set(preset.hemiLight.ground);
  hemiLight.intensity = preset.hemiLight.intensity;

  ambientLight.color.set(preset.ambientLight.color);
  ambientLight.intensity = preset.ambientLight.intensity;

  dirLight.color.set(preset.dirLight.color);
  dirLight.intensity = preset.dirLight.intensity;
  dirLight.position.set(...preset.dirLight.position);

  if (rimLight && preset.rimLight) {
    rimLight.color.set(preset.rimLight.color);
    rimLight.intensity = preset.rimLight.intensity;
    rimLight.position.set(...preset.rimLight.position);
  }

  if (fillLight && preset.fillLight) {
    fillLight.color.set(preset.fillLight.color);
    fillLight.intensity = preset.fillLight.intensity;
    fillLight.position.set(...preset.fillLight.position);
  }

  // 2. Bầu trời và Sương mù (Fog) theo ngữ cảnh Mode
  const isCityOrDrama = state.currentMode === 'city' || state.currentMode === 'drama';

  if (isCityOrDrama) {
    scene.environment = null;
    const skyTex = createSkyGradientTexture(preset.sky.top, preset.sky.bottom);
    if (skyTex) {
      scene.background = skyTex;
    } else {
      scene.background = new THREE.Color(preset.sky.bottom);
    }

    if (scene.fog) {
      scene.fog.color.set(preset.fog.color);
      scene.fog.density = preset.fog.density;
    }
  } else {
    // Chế độ Studio / Animals / Walker / Lab: Giữ nền trung tính tối và Env Map Studio
    scene.environment = state.studioEnvMap || null;
    scene.background = new THREE.Color(0x0f172a);
    if (scene.fog) {
      scene.fog.color.set(0x0f172a);
      scene.fog.density = 0.025;
    }
  }
}
